import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useArchiveExclusionGroup, useExclusionGroup } from '../../api/exclusion-groups';
import { useExperiments } from '../../api/experiments';
import { useIsAdmin } from '../../auth/useAuth';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { ChangelogTimeline } from '../../components/ChangelogTimeline';
import { PageHeader } from '../../components/PageHeader';
import { SegmentedBar } from '../../components/SegmentedBar';
import { Table } from '../../components/Table';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import shared from '../shared.module.css';
import styles from './ExclusionGroupDetailPage.module.css';

export function ExclusionGroupDetailPage() {
	const { id, groupId } = useParams<{ id: string; groupId: string }>();
	const navigate = useNavigate();
	const isAdmin = useIsAdmin();

	const { data: group, isLoading, error } = useExclusionGroup(id ?? '', groupId ?? '');
	const { data: experiments } = useExperiments(id ?? '');
	const archiveGroup = useArchiveExclusionGroup(id ?? '', groupId ?? '');

	const [confirmArchive, setConfirmArchive] = useState(false);

	const listBreadcrumbs = useProjectBreadcrumbs('Exclusion Groups');
	const detailBreadcrumbs = useProjectBreadcrumbs('Exclusion Groups', group?.name ?? '');

	const experimentNames: Record<string, string> = {};
	for (const exp of experiments ?? []) {
		experimentNames[exp.id] = exp.name;
	}

	async function handleArchive() {
		if (!confirmArchive) {
			setConfirmArchive(true);
			return;
		}
		await archiveGroup.mutateAsync();
		navigate(`/projects/${id}/exclusion-groups`);
	}

	if (isLoading) {
		return (
			<div>
				<PageHeader title="Loading…" breadcrumbs={listBreadcrumbs} />
			</div>
		);
	}

	if (error || !group) {
		return (
			<div>
				<PageHeader title="Exclusion Group" breadcrumbs={listBreadcrumbs} />
				<Card>
					<p className={shared.errorMessage}>
						{error instanceof Error ? error.message : 'Exclusion group not found'}
					</p>
				</Card>
			</div>
		);
	}

	const sortedAllocations = [...group.allocations].sort((a, b) => a.range_start - b.range_start);

	const allocationColumns = [
		{ key: 'experiment', label: 'Experiment' },
		{ key: 'range_start', label: 'Range Start', mono: true },
		{ key: 'range_end', label: 'Range End', mono: true },
		{ key: 'pct', label: '%', mono: true },
	];

	return (
		<div>
			<PageHeader
				title={group.name}
				breadcrumbs={detailBreadcrumbs}
				actions={
					isAdmin ? (
						<div className={styles.headerActions}>
							{confirmArchive ? (
								<>
									<span className={styles.confirmText}>Archive this group?</span>
									<Button variant="secondary" onClick={() => setConfirmArchive(false)}>
										Cancel
									</Button>
									<Button
										variant="destructive"
										onClick={handleArchive}
										loading={archiveGroup.isPending}
									>
										Confirm Archive
									</Button>
								</>
							) : (
								<Button variant="destructive" onClick={handleArchive}>
									Archive
								</Button>
							)}
						</div>
					) : undefined
				}
			/>

			<div className={shared.content}>
				{archiveGroup.error && (
					<p className={shared.mutationError}>
						{archiveGroup.error instanceof Error
							? archiveGroup.error.message
							: 'Failed to archive group'}
					</p>
				)}

				<Card>
					<h2 className={shared.cardHeading}>Details</h2>
					<dl className={shared.detailList}>
						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>ID</dt>
							<dd className={[shared.detailValue, shared.mono].join(' ')}>{group.id}</dd>
						</div>
						{group.description && (
							<div className={shared.detailRow}>
								<dt className={shared.detailLabel}>Description</dt>
								<dd className={shared.detailValue}>{group.description}</dd>
							</div>
						)}
						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>Created by</dt>
							<dd className={[shared.detailValue, shared.mono].join(' ')}>{group.created_by}</dd>
						</div>
					</dl>
				</Card>

				<Card>
					<h2 className={shared.cardHeading}>Allocations</h2>
					<div className={styles.allocationBar}>
						<SegmentedBar
							segments={[...group.allocations]
								.sort((a, b) => a.range_start - b.range_start)
								.map((alloc) => {
									const expName = experimentNames[alloc.experiment_id] ?? alloc.experiment_id;
									return {
										key: `${alloc.experiment_id}-${alloc.range_start}-${alloc.range_end}`,
										label: expName,
										value: alloc.range_end - alloc.range_start,
										title: `${expName}: ${(((alloc.range_end - alloc.range_start) / 10000) * 100).toFixed(1)}%`,
									};
								})}
							denominator={10000}
						/>
					</div>
					<div className={styles.allocationTableWrapper}>
						<Table>
							<Table.Header columns={allocationColumns} />
							<Table.Body>
								{sortedAllocations.map((alloc, i) => (
									// biome-ignore lint/suspicious/noArrayIndexKey: read-only stable list
									<Table.Row key={i}>
										<Table.Cell>
											{experimentNames[alloc.experiment_id] ?? alloc.experiment_id}
										</Table.Cell>
										<Table.Cell mono>{alloc.range_start}</Table.Cell>
										<Table.Cell mono>{alloc.range_end}</Table.Cell>
										<Table.Cell mono>
											{(((alloc.range_end - alloc.range_start) / 10000) * 100).toFixed(1)}%
										</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>
						</Table>
					</div>
				</Card>

				<Card>
					<h2 className={shared.cardHeading}>Activity</h2>
					<ChangelogTimeline
						projectId={id ?? ''}
						resourceType="exclusion_group"
						resourceId={groupId ?? ''}
					/>
				</Card>
			</div>
		</div>
	);
}
